import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Settings2, Trash2, X } from 'lucide-react';
import { api } from '../api/client';
import { useSocle } from '../hooks/useSocle';
import { fmtCHF, fmtCHF2, fmtMoisFr } from '../utils/format';
import { MOIS_LABELS, Prestation, ScenarioBudget, TagRevenu } from '../types';
import type { GrilleCharges, LigneCompte, RubriqueCharge } from '../types/charges';
import { CLASSES_LABELS } from '../types/charges';
import { BadgeTagRevenu, ChoixTagScenario } from '../components/TagScenarioRevenu';
import { SelectAnnee } from '../components/SelectAnnee';

/**
 * Charges d'exploitation : le reste des dépenses, hors salaires (module
 * Salaires) et hors loyers (module Loyers, qui fait foi pour les comptes 600x).
 *
 * Une ligne = un compte du plan comptable au numéro **nu**. Les suffixes
 * analytiques de la comptabilité (-CCC, -ANT, -ENM…), qui n'existent que faute
 * de comptabilité analytique dans l'outil comptable, sont remplacés ici par une
 * vraie imputation : prestation et/ou site, éditables sur la fiche du compte.
 *
 * Clic sur le numéro → fiche du compte ; double-clic sur une cellule → valeur
 * figée, puis « Corriger ». Les montants sont signés : un produit exceptionnel
 * est négatif.
 */
export function ChargesPage() {
  const maintenant = new Date();
  const [annee, setAnnee] = useState(maintenant.getFullYear());
  // undefined = socle pas encore connu ; la grille attend avant de charger
  const socleId = useSocle();
  const [scenarioId, setScenarioId] = useState<number | null | undefined>(undefined);
  const [grille, setGrille] = useState<GrilleCharges | null>(null);
  const [rubriques, setRubriques] = useState<RubriqueCharge[]>([]);
  const [prestations, setPrestations] = useState<Prestation[]>([]);
  const [sites, setSites] = useState<Array<{ id: number; nom: string }>>([]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [chargement, setChargement] = useState(true);
  const [ficheCompte, setFicheCompte] = useState<LigneCompte | null | 'nouveau'>(null);
  const [edition, setEdition] = useState<{ compte: LigneCompte; mois: number } | null>(null);

  const charger = useCallback(() => {
    if (scenarioId === undefined) return; // on attend de connaître le socle
    setErreur(null);
    api.get<GrilleCharges>(`/charges/grille?annee=${annee}&scenario=${scenarioId ?? 'definitif'}`)
      .then(setGrille)
      .catch((e) => setErreur(e.message))
      .finally(() => setChargement(false));
  }, [annee, scenarioId]);

  useEffect(() => {
    if (scenarioId === undefined && socleId !== undefined) setScenarioId(socleId);
  }, [socleId, scenarioId]);

  useEffect(() => { charger(); }, [charger]);

  useEffect(() => {
    api.get<RubriqueCharge[]>('/charges/rubriques').then(setRubriques).catch(() => {});
    api.get<Prestation[]>('/prestations').then(setPrestations).catch(() => {});
    api.get<Array<{ id: number; nom: string }>>('/loyers/sites').then(setSites).catch(() => {});
  }, []);

  const scenariosActifs = useMemo(
    () => (grille?.scenarios ?? []).filter((s) => !s.archive),
    [grille]
  );
  const variantes = useMemo(() => scenariosActifs.filter((s) => s.id !== socleId), [scenariosActifs, socleId]);
  const scenarioNom = scenarioId != null
    ? scenariosActifs.find((s) => s.id === scenarioId)?.nom ?? null
    : null;

  const moisCourant = annee === maintenant.getFullYear()
    ? maintenant.getMonth() + 1
    : annee < maintenant.getFullYear() ? 13 : 0;
  const classesColonne = (m: number) =>
    m < moisCourant ? ' col-passe' : m === moisCourant ? ' col-courant' : '';

  // Comptes regroupés par rubrique, dans l'ordre rendu par le serveur
  const groupes = useMemo(() => {
    const ordre: string[] = [];
    const parRubrique = new Map<string, LigneCompte[]>();
    for (const c of grille?.comptes ?? []) {
      if (!parRubrique.has(c.rubriqueCode)) { parRubrique.set(c.rubriqueCode, []); ordre.push(c.rubriqueCode); }
      parRubrique.get(c.rubriqueCode)!.push(c);
    }
    return ordre.map((code) => ({
      code,
      libelle: parRubrique.get(code)![0].rubriqueLibelle,
      comptes: parRubrique.get(code)!,
      sousTotal: (grille?.sousTotaux ?? []).find((s) => s.code === code),
    }));
  }, [grille]);

  const parClasse = useMemo(() => {
    const somme = new Map<string, number>();
    for (const s of grille?.sousTotaux ?? []) {
      somme.set(s.classe, (somme.get(s.classe) ?? 0) + s.annuel);
    }
    return somme;
  }, [grille]);

  if (chargement) {
    return <div className="flex items-center justify-center" style={{ height: 240 }}><div className="spinner" /></div>;
  }

  function renduCellule(compte: LigneCompte, m: number) {
    const c = compte.mois[m - 1];
    const projete = c.tag === 'projete';
    const moisIso = `${annee}-${String(m).padStart(2, '0')}`;
    const previsionReportee = !projete && m > moisCourant && c.montant !== 0 &&
      c.moisEffet != null && c.moisEffet.slice(0, 7) < moisIso;

    const infobulle = [
      `${compte.numero} ${compte.libelle} — ${MOIS_LABELS[m - 1]} ${annee}`,
      `CHF ${fmtCHF2(c.montant)}`,
      previsionReportee
        ? 'Prévision : report de la dernière valeur en vigueur (aucune décision ne prend effet ce mois-ci)'
        : null,
      c.changement ? `Changement (${c.changement.tag}) : ${c.changement.commentaire}` : null,
      'Double-cliquer pour modifier',
    ].filter(Boolean).join('\n');

    return (
      <td
        key={m}
        className={`cell-mois${classesColonne(m)}${projete ? ' cell-projete' : ''}${c.montant === 0 ? ' cell-vide' : ''}${previsionReportee ? ' cell-report' : ''}`}
        title={infobulle}
        onDoubleClick={() => setEdition({ compte, mois: m })}
      >
        {c.changement && (
          <span className={`cell-flag cell-flag--${c.changement.tag === 'projete' ? 'projete' : 'definitif'}`} />
        )}
        <span className="cell-principal" style={c.montant < 0 ? { color: 'var(--ok)' } : undefined}>
          {c.montant !== 0 ? fmtCHF(c.montant) : '—'}
        </span>
      </td>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div className="flex items-center gap-8">
          <h1 className="page-title">Charges</h1>
          <span className="stat-pill">CHF {fmtCHF(grille?.totalAnnuel ?? 0)} / {annee}</span>
          {scenarioId == null
            ? <span className="stat-pill" style={{ color: 'var(--ink-2)' }}
                title="Décisions arrêtées seulement — les mois à venir n'y sont qu'un report">décidé seulement</span>
            : scenarioId === socleId
              ? <span className="stat-pill" style={{ color: 'var(--accent)' }}
                  title="Le budget en l'état : décisions arrêtées plus hypothèses retenues">budget</span>
              : <span className="stat-pill" style={{ color: 'var(--warn)' }}>variante · {scenarioNom}</span>}
        </div>
        <div className="flex gap-8 items-center">
          <SelectAnnee annee={annee} onChange={setAnnee} />
          <button className="btn btn-accent btn-sm" onClick={() => setFicheCompte('nouveau')}>
            <Plus className="icon-sm" /> Nouveau compte
          </button>
        </div>
      </div>

      {erreur && <div className="card mb-16 p-16 text-err">{erreur}</div>}

      <div className="kpi-row mb-16">
        <div className="kpi-card">
          <span className="kpi-card__label">Charges de matériel</span>
          <span className="kpi-card__value">{fmtCHF(parClasse.get('materiel') ?? 0)}<span className="kpi-card__suffix">CHF</span></span>
        </div>
        <div className="kpi-card">
          <span className="kpi-card__label">Autres charges d'exploitation</span>
          <span className="kpi-card__value">{fmtCHF(parClasse.get('exploitation') ?? 0)}<span className="kpi-card__suffix">CHF</span></span>
        </div>
        <div className="kpi-card">
          <span className="kpi-card__label">Amortissements & financier</span>
          <span className="kpi-card__value">
            {fmtCHF((parClasse.get('amortissement') ?? 0) + (parClasse.get('financier') ?? 0))}
            <span className="kpi-card__suffix">CHF</span>
          </span>
        </div>
        <div className="kpi-card">
          <span className="kpi-card__label">Comptes actifs</span>
          <span className="kpi-card__value">{grille?.comptes.length ?? 0}</span>
        </div>
      </div>

      <div className="toolbar">
        <div className="flex gap-8 items-center" title="Décisions prises seules, ou avec les hypothèses d'un scénario">
          <div className="selectable-group">
            <button
              className={`selectable${scenarioId != null && scenarioId === socleId ? ' active' : ''}`}
              disabled={socleId == null}
              title="Le budget en l'état : décisions arrêtées plus hypothèses retenues"
              onClick={() => setScenarioId(socleId ?? null)}
            >
              Budget
            </button>
            <button
              className={`selectable${scenarioId != null && scenarioId !== socleId ? ' active' : ''}`}
              disabled={variantes.length === 0}
              title={variantes.length === 0 ? 'Aucune variante — en créer une dans Scénarios' : 'Une variante du budget'}
              onClick={() => setScenarioId(variantes[0]?.id ?? null)}
            >
              Variante
            </button>
            <button
              className={`selectable${scenarioId == null ? ' active' : ''}`}
              title="Décisions arrêtées seulement, sans aucune hypothèse — les mois à venir n'y sont qu'un report"
              onClick={() => setScenarioId(null)}
            >
              Décidé seulement
            </button>
          </div>
          {scenarioId != null && scenarioId !== socleId && (
            <select className="sort-select" value={scenarioId} onChange={(e) => setScenarioId(Number(e.target.value))}>
              {variantes.map((s) => <option key={s.id} value={s.id}>{s.nom}</option>)}
            </select>
          )}
          <button className="btn btn-icon" title="Gérer les scénarios"
            onClick={() => { window.location.hash = 'scenarios'; }}>
            <Settings2 className="icon-sm" />
          </button>
        </div>
      </div>

      <div className="grille-container">
        <table className="table table-compact grille-salaires">
          <thead>
            <tr>
              <th className="cell-nom">Compte</th>
              {MOIS_LABELS.map((label, i) => (
                <th key={label} className={`cell-mois${classesColonne(i + 1)}`}>
                  {label}
                  {i + 1 === moisCourant && <span className="marqueur-courant" title="Mois en cours" />}
                </th>
              ))}
              <th className="cell-total">Total {annee}</th>
            </tr>
          </thead>
          <tbody>
            {groupes.map(({ code, libelle, comptes, sousTotal }) => [
              <tr key={`g-${code}`} className="ligne-groupe">
                <td className="cell-nom">{code} · {libelle}</td>
                {Array.from({ length: 12 }, (_, i) => (
                  <td key={i} className={`cell-mois${classesColonne(i + 1)}`}>
                    <span className="cell-principal">
                      {sousTotal && sousTotal.parMois[i] !== 0 ? fmtCHF(sousTotal.parMois[i]) : ''}
                    </span>
                  </td>
                ))}
                <td className="cell-total">
                  <span className="cell-principal">{sousTotal ? fmtCHF(sousTotal.annuel) : ''}</span>
                </td>
              </tr>,
              ...comptes.map((c) => (
                <tr key={c.id}>
                  <td className="cell-nom">
                    <span
                      className="nom-employe"
                      style={{ cursor: 'pointer' }}
                      title={[c.note, c.prestationLibelle ? `Prestation : ${c.prestationLibelle}` : null,
                              c.siteNom ? `Site : ${c.siteNom}` : null, 'Ouvrir la fiche du compte']
                        .filter(Boolean).join('\n')}
                      onClick={() => setFicheCompte(c)}
                    >
                      <span className="text-xs text-ink-3">{c.numero}</span> {c.libelle}
                    </span>
                    {c.prestationLibelle && <span className="badge badge-sm" style={{ marginLeft: 6 }}>{c.prestationLibelle}</span>}
                    {c.siteNom && <span className="badge badge-sm" style={{ marginLeft: 6 }}>{c.siteNom}</span>}
                  </td>
                  {Array.from({ length: 12 }, (_, i) => renduCellule(c, i + 1))}
                  <td className="cell-total">
                    <span className="cell-principal">{c.totalAnnuel !== 0 ? fmtCHF(c.totalAnnuel) : '—'}</span>
                  </td>
                </tr>
              )),
            ])}
            {(grille?.comptes.length ?? 0) === 0 && (
              <tr><td colSpan={14} className="text-ink-3">
                Aucun compte — lancer <code>scripts/import-charges-2025.ts</code> ou créer un compte.
              </td></tr>
            )}
            <tr className="ligne-totaux">
              <td className="cell-nom">Total</td>
              {(grille?.totauxMensuels ?? new Array(12).fill(0)).map((v, i) => (
                <td key={i} className={`cell-mois${classesColonne(i + 1)}`}>
                  <span className="cell-principal">{v !== 0 ? fmtCHF(v) : '—'}</span>
                </td>
              ))}
              <td className="cell-total">
                <span className="cell-principal">{fmtCHF(grille?.totalAnnuel ?? 0)}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="mt-16 text-xs text-ink-3">
        <p>
          Les salaires (module Salaires) et les loyers, parkings et charges liées aux baux (module Loyers)
          ne figurent pas ici : chaque donnée vit là où elle est décidée, et la vue Budget les rassemble.
        </p>
        <p>
          L'imputation par prestation ou par site, sur la fiche d'un compte, remplace les suffixes
          analytiques de la comptabilité (-CCC, -ANT, -ENM…). Pour ventiler un même compte sur plusieurs
          prestations, créer une seconde ligne avec le même numéro et une autre imputation.
        </p>
        {(grille?.sousTotaux ?? []).some((s) => s.classe === 'exceptionnel') && (
          <p>Un produit s'affiche en négatif (compte 8510 par exemple) : il augmente le résultat.</p>
        )}
      </div>

      {edition && (
        <EditionCharge
          compte={edition.compte}
          mois={edition.mois}
          annee={annee}
          scenarios={grille?.scenarios ?? []}
          scenarioCourant={scenarioId ?? null}
          moisCourant={moisCourant}
          onFermer={() => setEdition(null)}
          onModifie={() => { setEdition(null); charger(); }}
        />
      )}
      {ficheCompte && (
        <CompteModal
          compte={ficheCompte === 'nouveau' ? null : ficheCompte}
          rubriques={rubriques}
          prestations={prestations.filter((p) => p.actif)}
          sites={sites}
          onFermer={() => setFicheCompte(null)}
          onModifie={() => { setFicheCompte(null); charger(); }}
        />
      )}
    </div>
  );
}

// ── Édition du montant d'un mois ───────────────────────────────────────────

function EditionCharge({ compte, mois, annee, scenarios, scenarioCourant, moisCourant, onFermer, onModifie }: {
  compte: LigneCompte;
  mois: number;
  annee: number;
  scenarios: ScenarioBudget[];
  scenarioCourant: number | null;
  moisCourant: number;
  onFermer: () => void;
  onModifie: () => void;
}) {
  const cellule = compte.mois[mois - 1];
  const [moisEffet, setMoisEffet] = useState(`${annee}-${String(mois).padStart(2, '0')}`);
  const [montant, setMontant] = useState(String(cellule.montant));
  const [tag, setTag] = useState<'definitif' | 'projete'>(scenarioCourant != null ? 'projete' : 'definitif');
  const [scenarioId, setScenarioId] = useState<number | null>(scenarioCourant);
  const [commentaireManuel, setCommentaireManuel] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  // La valeur affichée est figée : il faut cliquer « Corriger » pour saisir.
  const [deverrouille, setDeverrouille] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onFermer(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onFermer]);

  const nomScenario = (id: number | null) =>
    id == null ? null : scenarios.find((s) => s.id === id)?.nom ?? `#${id}`;

  // Mois futur dont la valeur n'est qu'un report = prévision, pas un définitif
  const futur = mois > moisCourant;
  function badgeValeur(t: TagRevenu | null, moisEffetEvt: string | null) {
    if (!t) return null;
    if (t !== 'projete' && futur && moisEffetEvt != null &&
        moisEffetEvt.slice(0, 7) < `${annee}-${String(mois).padStart(2, '0')}`) {
      const [a, m] = moisEffetEvt.split('-');
      return (
        <span
          className="badge badge-warn badge-sm"
          title={`Prévision : report du dernier ${t === 'consolide' ? 'consolidé' : 'définitif'} en vigueur (${MOIS_LABELS[Number(m) - 1].toLowerCase()} ${a}). Aucune décision ne prend effet ce mois-ci.`}
        >
          prévision
        </span>
      );
    }
    return <BadgeTagRevenu tag={t} scenarioNom={nomScenario(scenarioCourant)} />;
  }

  const montantNum = Number(montant);
  const change = Number.isFinite(montantNum) && Math.abs(montantNum - cellule.montant) > 0.004;

  const commentaireAuto = useMemo(
    () => `Édition : ${fmtCHF2(cellule.montant)} → ${fmtCHF2(montantNum)}, dès ${moisEffet}`,
    [cellule.montant, montantNum, moisEffet]
  );
  const commentaire = commentaireManuel ?? commentaireAuto;

  const valide =
    /^\d{4}-\d{2}$/.test(moisEffet) && change &&
    commentaire.trim().length > 0 &&
    (tag === 'definitif' || scenarioId != null);

  async function enregistrer() {
    if (!valide || enCours) return;
    setEnCours(true);
    setErreur(null);
    try {
      await api.post(`/charges/comptes/${compte.id}/evenements`, {
        moisEffet, montant: montantNum, tag,
        ...(tag === 'projete' ? { scenarioId } : {}),
        commentaire: commentaire.trim(),
      });
      onModifie();
    } catch (e) {
      setErreur((e as Error).message);
      setEnCours(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onFermer}>
      <div className="modal" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">
            {compte.numero} {compte.libelle} — {MOIS_LABELS[mois - 1]} {annee}
          </div>
          <button className="close-btn" onClick={onFermer} title="Fermer (Esc)"><X className="icon-sm" /></button>
        </div>
        <div className="modal-body" style={{ overflowY: 'auto' }}>
          {!deverrouille ? (
            <div className="formulaire-changement mb-16">
              <div className="flex items-center gap-8 text-sm">
                <span>Montant : <strong>CHF {fmtCHF2(cellule.montant)}</strong></span>
                {badgeValeur(cellule.tag, cellule.moisEffet)}
              </div>
              {cellule.changement && (
                <div className="text-xs text-ink-3 mt-8">
                  Événement de ce mois : {cellule.changement.commentaire}
                </div>
              )}
              <div className="flex items-center justify-between mt-8">
                <span className="text-xs text-ink-3">Valeur figée — corriger crée un nouvel événement tracé.</span>
                <button className="btn btn-accent btn-sm" onClick={() => setDeverrouille(true)}>Corriger ✎</button>
              </div>
            </div>
          ) : (
            <div className="formulaire-changement mb-16">
              <div className="formulaire-changement__titre">Nouveau montant à partir d'un mois donné</div>
              <div className="flex gap-12 flex-wrap items-end">
                <div className="form-group">
                  <label className="form-label">À partir de</label>
                  <input type="month" className="form-input" value={moisEffet}
                    onChange={(e) => setMoisEffet(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Montant (CHF/mois)</label>
                  <input type="number" className="form-input" style={{ width: 130 }} step={0.05} autoFocus
                    value={montant} onChange={(e) => setMontant(e.target.value)}
                    onFocus={(e) => e.target.select()} />
                </div>
                <div className="form-group">
                  <label className="form-label">Statut</label>
                  <ChoixTagScenario tag={tag} scenarioId={scenarioId} scenarios={scenarios}
                    onChange={(t, s) => { setTag(t); setScenarioId(s); }} />
                </div>
              </div>
              <div className="form-group mt-8">
                <label className="form-label">Commentaire (obligatoire)</label>
                <input className="form-input w-full" value={commentaire}
                  onChange={(e) => setCommentaireManuel(e.target.value)} />
              </div>
              <p className="text-xs text-ink-3">
                Le montant vaut à partir de ce mois et jusqu'au prochain changement. Un produit se saisit
                en négatif.
              </p>
              <div className="flex justify-end">
                <button className="btn btn-accent" disabled={!valide || enCours} onClick={enregistrer}>
                  {enCours ? 'Enregistrement…' : 'Enregistrer'}
                </button>
              </div>
            </div>
          )}

          {erreur && <div className="text-err text-sm mb-8">{erreur}</div>}

          <div className="text-xs text-ink-3">Historique du compte</div>
          <table className="table table-compact">
            <thead>
              <tr><th>Dès</th><th className="text-right">Montant</th><th>Statut</th><th>Commentaire</th></tr>
            </thead>
            <tbody>
              {compte.evenements.slice().reverse().map((e) => (
                <tr key={e.id}>
                  <td className="tabular-nums">{fmtMoisFr(e.mois_effet)}</td>
                  <td className="tabular-nums text-right">{fmtCHF2(e.montant)}</td>
                  <td><BadgeTagRevenu tag={e.tag} scenarioNom={nomScenario(e.scenario_id)} /></td>
                  <td className="text-sm text-ink-2">{e.commentaire}</td>
                </tr>
              ))}
              {compte.evenements.length === 0 && (
                <tr><td colSpan={4} className="text-ink-3">Aucun montant enregistré.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Fiche du compte (création / édition, imputation analytique) ─────────────

function CompteModal({ compte, rubriques, prestations, sites, onFermer, onModifie }: {
  compte: LigneCompte | null; // null = création
  rubriques: RubriqueCharge[];
  prestations: Prestation[];
  sites: Array<{ id: number; nom: string }>;
  onFermer: () => void;
  onModifie: () => void;
}) {
  const [numero, setNumero] = useState(compte?.numero ?? '');
  const [libelle, setLibelle] = useState(compte?.libelle ?? '');
  const [rubriqueId, setRubriqueId] = useState<number | null>(compte?.rubriqueId ?? null);
  const [prestationId, setPrestationId] = useState<number | null>(compte?.prestationId ?? null);
  const [siteId, setSiteId] = useState<number | null>(compte?.siteId ?? null);
  const [note, setNote] = useState(compte?.note ?? '');
  const [actif, setActif] = useState(compte?.actif ?? true);
  const [commentaire, setCommentaire] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onFermer(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onFermer]);

  // Rubrique proposée d'après les deux premiers chiffres du numéro saisi
  useEffect(() => {
    if (compte || rubriqueId != null || numero.length < 2) return;
    const suggeree = rubriques.find((r) => r.code === numero.slice(0, 2));
    if (suggeree) setRubriqueId(suggeree.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numero, rubriques]);

  const valide = numero.trim().length > 0 && libelle.trim().length > 0 &&
    rubriqueId != null && commentaire.trim().length > 0;

  async function enregistrer() {
    if (!valide || enCours) return;
    setEnCours(true);
    setErreur(null);
    const corps = {
      numero: numero.trim(), libelle: libelle.trim(), rubriqueId,
      prestationId, siteId, note: note.trim() || null, commentaire: commentaire.trim(),
    };
    try {
      if (compte) await api.put(`/charges/comptes/${compte.id}`, { ...corps, actif });
      else await api.post('/charges/comptes', corps);
      onModifie();
    } catch (e) {
      setErreur((e as Error).message);
      setEnCours(false);
    }
  }

  async function supprimer() {
    if (!compte) return;
    if (!window.confirm(`Supprimer le compte ${compte.numero} ${compte.libelle} ?`)) return;
    const c = window.prompt('Commentaire de suppression (obligatoire) :');
    if (!c || !c.trim()) return;
    setErreur(null);
    try {
      await api.delete(`/charges/comptes/${compte.id}`, { commentaire: c.trim() });
      onModifie();
    } catch (e) {
      setErreur((e as Error).message);
    }
  }

  return (
    <div className="modal-overlay" onClick={onFermer}>
      <div className="modal" style={{ maxWidth: 620 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div className="modal-title">
              {compte ? `${compte.numero} ${compte.libelle}` : 'Nouveau compte de charges'}
            </div>
            {compte && (
              <div className="text-xs text-ink-3">
                {compte.rubriqueCode} · {compte.rubriqueLibelle} — {CLASSES_LABELS[compte.classe] ?? compte.classe}
              </div>
            )}
          </div>
          <button className="close-btn" onClick={onFermer} title="Fermer (Esc)"><X className="icon-sm" /></button>
        </div>
        <div className="modal-body" style={{ overflowY: 'auto' }}>
          <div className="flex flex-col gap-12">
            <div className="flex gap-12 flex-wrap">
              <div className="form-group">
                <label className="form-label">Numéro</label>
                <input className="form-input" style={{ width: 110 }} value={numero}
                  onChange={(e) => setNumero(e.target.value)} placeholder="6530" />
              </div>
              <div className="form-group" style={{ flex: 1, minWidth: 220 }}>
                <label className="form-label">Libellé</label>
                <input className="form-input w-full" value={libelle} onChange={(e) => setLibelle(e.target.value)} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Rubrique</label>
              <select className="form-input w-full" value={rubriqueId ?? ''}
                onChange={(e) => setRubriqueId(e.target.value ? Number(e.target.value) : null)}>
                <option value="">— choisir —</option>
                {rubriques.map((r) => (
                  <option key={r.id} value={r.id}>{r.code} · {r.libelle}</option>
                ))}
              </select>
            </div>

            <div className="formulaire-changement">
              <div className="formulaire-changement__titre">Imputation analytique (facultative)</div>
              <div className="flex gap-12 flex-wrap">
                <div className="form-group" style={{ flex: 1, minWidth: 200 }}>
                  <label className="form-label">Prestation</label>
                  <select className="form-input w-full" value={prestationId ?? ''}
                    onChange={(e) => setPrestationId(e.target.value ? Number(e.target.value) : null)}>
                    <option value="">— aucune (charge transversale) —</option>
                    {prestations.map((p) => (
                      <option key={p.id} value={p.id}>{p.groupe ? `${p.groupe} — ${p.libelle}` : p.libelle}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group" style={{ flex: 1, minWidth: 180 }}>
                  <label className="form-label">Site</label>
                  <select className="form-input w-full" value={siteId ?? ''}
                    onChange={(e) => setSiteId(e.target.value ? Number(e.target.value) : null)}>
                    <option value="">— aucun —</option>
                    {sites.map((s) => <option key={s.id} value={s.id}>{s.nom}</option>)}
                  </select>
                </div>
              </div>
              <p className="text-xs text-ink-3">
                Remplace les suffixes analytiques de la comptabilité (-CCC, -ANT, -ENM…). Pour ventiler un
                même compte sur deux prestations, créer une seconde ligne avec le même numéro.
              </p>
            </div>

            <div className="form-group">
              <label className="form-label">Note</label>
              <input className="form-input w-full" value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            {compte && (
              <label className="toggle-label">
                <span className="toggle">
                  <input type="checkbox" className="toggle-input" checked={actif}
                    onChange={(e) => setActif(e.target.checked)} />
                  <span className="toggle-slider" />
                </span>
                <span className="text-sm">
                  Compte actif <span className="text-ink-3">— désactiver le retire de la grille sans perdre l'historique</span>
                </span>
              </label>
            )}
            <div className="form-group">
              <label className="form-label">Commentaire de modification (obligatoire)</label>
              <input className="form-input w-full" placeholder="Motif de la création ou de la modification…"
                value={commentaire} onChange={(e) => setCommentaire(e.target.value)} />
            </div>
            {erreur && <div className="text-err text-sm">{erreur}</div>}
            <div className="flex justify-between">
              {compte ? (
                <button className="btn btn-icon" title="Supprimer le compte" onClick={supprimer}>
                  <Trash2 className="icon-sm" />
                </button>
              ) : <span />}
              <div className="flex gap-8">
                <button className="btn" onClick={onFermer}>Annuler</button>
                <button className="btn btn-accent" disabled={!valide || enCours} onClick={enregistrer}>
                  {enCours ? 'Enregistrement…' : 'Enregistrer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
